import { useTranslation } from "react-i18next";
import { URLS } from "../../../../services/urls";
import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../../../services/axios";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmDeletion } from "../../../../components/dletion";
import { ErrorMessage } from "@hookform/error-message";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ResumePDF from "./ResumePDF";

const ManageReferences = ({ resume, handleNext, handlePrev }) => {
  const { t }      = useTranslation();
  const { action } = useParams();
  const { auth }   = useSelector((state) => state.auth);

  const [references, setReferences]     = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [toEdit, setToEdit]             = useState(null);
  const [loading, setLoading]           = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({});
  const formRef = useRef(null);

  // ─── Fetch references, filter client-side by profile ─────────────────────
  const loadReferences = useCallback(() => {
    if (!action || action === "create") return;
    setFetchLoading(true);
    API.get(URLS.USERS.PROFILE.details(action))
      .then(({ data }) => {
        setReferences(data.reference || []);
        setFetchLoading(false);
      })
      .catch(() => setFetchLoading(false));
  }, [action]);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  // ─── Edit ──────────────────────────────────────────────────────────────────
  const handleChangeReference = (ref) => {
    setToEdit(ref);
    reset({ email: ref.email, name: ref.name, phone: ref.phone_number, role: ref.role });
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteReference = (ref) => {
    const onSuccess = () =>
      setReferences((prev) => prev.filter((r) => r.id !== ref.id));
    confirmDeletion(URLS.USERS.PROFILE.REFERENCE.update(ref.id), t, onSuccess);
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = (data) => {
    setLoading(true);
    const fd = new FormData();
    fd.append("email",        data.email);
    fd.append("phone_number", data.phone);
    fd.append("name",         data.name);
    fd.append("role",         data.role);
    fd.append("profile",      action);

    if (!toEdit) {
      API.post(URLS.USERS.PROFILE.REFERENCE.create, fd)
        .then(({ data: resp }) => {
          setLoading(false);
          reset();
          toast.success(t("textReferenceAdded"));
          setReferences((prev) => [resp, ...prev]);
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.detail ??
            err?.response?.data?.messagerror ??
            t("textReferenceAddingError")
          );
          setLoading(false);
        });
    } else {
      API.patch(URLS.USERS.PROFILE.REFERENCE.update(toEdit.id), fd)
        .then(({ data: resp }) => {
          setLoading(false);
          toast.success(t("referenceEdited"));
          setReferences((prev) =>
            prev.map((r) => (r.id === toEdit.id ? resp : r))
          );
          setToEdit(null);
          reset();
        })
        .catch((err) => {
          toast.error(err?.response?.data?.detail);
          setLoading(false);
        });
    }
  };

  // ─── PDF filename ──────────────────────────────────────────────────────────
  const fullName = [auth?.user?.first_name, auth?.user?.last_name]
    .filter(Boolean).join("_") || "cv";

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="panel-white mb-30" id="references">
      <div className="box-padding">
        <h5 className="icon-edu">{t("textReferences")}</h5>
        <div className="row mt-30">
          <div className="col-lg-9">

            {/* ── Form ── */}
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <div className="row">
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textName")}</label>
                    <input
                      {...register("name", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("textName")}
                    />
                    <ErrorMessage errors={errors} name="name"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textEmail")}</label>
                    <input
                      {...register("email", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="email"
                      placeholder={t("textEmail")}
                    />
                    <ErrorMessage errors={errors} name="email"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textPhone")}</label>
                    <input
                      {...register("phone", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="tel"
                      placeholder={t("textPhone")}
                    />
                    <ErrorMessage errors={errors} name="phone"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>
                <div className="col-lg-6 col-md-6">
                  <div className="form-group mb-30">
                    <label className="font-sm color-text-mutted mb-10">{t("textRole")}</label>
                    <input
                      {...register("role", { required: t("textFormFieldRequired") })}
                      className="form-control"
                      type="text"
                      placeholder={t("textRole")}
                    />
                    <ErrorMessage errors={errors} name="role"
                      render={({ message }) => <span className="text-primary">{message}</span>}
                    />
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="form-group mt-10 d-flex">
                    <button type="submit" className="btn btn-default btn-brand icon-tick ml-10">
                      {!toEdit ? t("textAddReference") : t("textEditReference")}
                      {loading && <span className="spinner-border spinner-border-sm ml-10" role="status" />}
                    </button>
                    {toEdit && (
                      <button
                        type="button"
                        className="btn btn-secondary ml-10"
                        onClick={() => { setToEdit(null); reset(); }}
                      >
                        {t("deleteActionCancel")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* ── Reference list ── */}
            {fetchLoading ? (
              <div className="d-flex mt-30">
                <div className="spinner-border m-auto spinner-border-sm" role="status" />
              </div>
            ) : (
              <div className="box-timeline mt-50">
                {references.map((ref) => (
                  <div key={ref.id} className="item-timeline">
                    <div className="timeline-year" />
                    <div className="timeline-info">
                      <h5 className="color-brand-1 mb-10">{ref.name}</h5>
                      {ref.role && <p className="color-text-paragraph-2 mb-5"><strong>{ref.role}</strong></p>}
                      <p className="color-text-paragraph-2 mb-15">
                        {t("textPhone")}: <Link to={`tel:${ref.phone_number}`}>{ref.phone_number}</Link><br />
                        {t("textEmail")}: <Link to={`mailto:${ref.email}`}>{ref.email}</Link>
                      </p>
                    </div>
                    <div className="timeline-actions">
                      <Link to="#" onClick={() => handleChangeReference(ref)} className="btn btn-editor position-inherit m-auto" />
                      <Link to="#" onClick={() => handleDeleteReference(ref)} className="btn btn-remove m-auto" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Navigation + PDF ── */}
            <div className="col-lg-12 mt-4">
              <div className="form-group mt-10 d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                <button type="button" onClick={handlePrev} className="btn btn-secondary btn-brand line-height-24">
                  {t("textPrevious")}
                </button>

                <Link to="/dashboard/my-resumes" className="btn btn-outline-secondary">
                  {t("textViewResumes")}
                </Link>

                {resume && (
                  <PDFDownloadLink
                    document={<ResumePDF resume={resume} user={auth?.user} />}
                    fileName={`CV_${fullName}.pdf`}
                    style={{ textDecoration: "none" }}
                    className="btn btn-default btn-brand ml-auto"
                  >
                    {({ loading: pdfLoading }) =>
                      pdfLoading ? t("textGeneratingPdf") : `⬇  ${t("textViewMyResume")}`
                    }
                  </PDFDownloadLink>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageReferences;
